







































































































































































































import { Component, Vue } from "vue-property-decorator";
import Visualization, { IUnityInstance } from "../components/Visualization.vue";
import DropUp from "@/components/controls/DropUp.vue";
import ShowHideItem from "@/components/controls/ShowHideItem.vue";
import { Crops, Layers, Systems } from "@/patchConstants";
import { IPatch, IPlant, IResult } from "@/types";
import DetailsPanel, { Tabs } from "@/components/controls/DetailsPanel.vue";
import FfgHeader from "@/components/FfgHeader.vue";
import ComplexEarthLogo from "@/components/ComplexEarthLogo.vue";
import { setSpecificTitle } from "@/router/titleWatcher";

Component.registerHooks(["beforeRouteLeave"]);

@Component({
  components: {
    ComplexEarthLogo,
    FfgHeader,
    DetailsPanel,
    ShowHideItem,
    DropUp,
    Visualization,
  },
})
export default class Patch extends Vue {
  private readonly layerOptions: Layers[] = Object.values(Layers);
  private readonly cropOptions: Crops[] = Object.values(Crops);
  private readonly systemOptions: Systems[] = Object.values(Systems);

  private layers: Layers[] = Object.values(Layers);
  private crops: Crops[] = [];
  private systems: Systems[] = [];

  private patch: IPatch | null = null;
  private result: IResult | null = null;
  private customResult: IResult | null = null;
  private initialised = false;
  private visualizationInstance: IUnityInstance | null = null;
  private selectedPlant: IPlant | null = null;
  private showAllInstances = false;

  private detailTab = Tabs.PATCH;
  private detailsPanelVisible = true;

  private get currentResult(): IResult | null {
    return this.customResult || this.result;
  }

  private get detailTabsEnabled() {
    if (this.selectedPlant) {
      return [Tabs.PATCH, Tabs.PLANT];
    } else {
      return [Tabs.PATCH];
    }
  }

  $refs!: {
    loaderBar: HTMLDivElement;
    loaderContainer: HTMLDivElement;
    visualization: Visualization;
  };

  private mounted() {
    fetch(`/api/patches/${this.$route.params["id"]}/`).then(
      async (response) => {
        if (response.ok) {
          this.patch = (await response.json()).data as IPatch;
          setSpecificTitle(this.patch.name);
        }
      }
    );
    fetch(`/api/patches/${this.$route.params["id"]}/result`).then(
      async (response) => {
        if (response.ok) {
          this.result = (await response.json())["data"]["result_json"];
          if (this.result === null) {
            alert(this.$tc("patch.no_data"));
          }
        } else {
          alert(`${response.statusText}\n\n${await response.text()}`);
        }
      }
    );
  }

  private get rainfallDisplay() {
    const mm = this.patch?.rainfall_annual;
    if (mm) {
      if (mm <= 1000) {
        return "Dry";
      }
      if (mm <= 2000) {
        return "Medium";
      }
      return "Wet";
    }
  }

  private get selectedInstanceCount() {
    if (this.selectedPlant && this.currentResult) {
      return Object.values(this.currentResult.plantPosition)
        .map((plantLayer) => {
          // workaround due to unexpected data from customResult
          if (!Array.isArray(plantLayer)) {
            return 0;
          }
          return plantLayer.filter(
            (position) => position.id === this.selectedPlant?.id
          ).length;
        })
        .reduce((p, c) => p + c, 0);
    }
    return 0;
  }

  private wikipediaLink(plant: IPlant) {
    return (
      `https://en.wikipedia.org/wiki/Special:Search` +
      `?search=${encodeURIComponent(
        plant.name_latin || plant.name_english
      )}&go=Go&ns0=1`
    );
  }

  private getSunDemandDisplay(plant: IPlant) {
    const INTERVALS = [
      {
        min: 0,
        max: 25,
        label: this.$tc("patch.plant.sun.shade").toString(),
      },
      {
        min: 25,
        max: 75,
        label: this.$tc("patch.plant.sun.semi").toString(),
      },
      {
        min: 75,
        max: 100,
        label: this.$tc("patch.plant.sun.sun").toString(),
      },
    ];

    return INTERVALS.filter(
      (interval) =>
        plant.sun_demand_min <= interval.max &&
        plant.sun_demand_max > interval.min
    )
      .map((interval) => interval.label)
      .join(" - ");
  }

  private onPlantSelected(plantId: string) {
    if (this.currentResult) {
      this.selectedPlant =
        this.currentResult.plantList.find((plant) => plant.id === plantId) ||
        null;
      if (this.selectedPlant) {
        this.detailsPanelVisible = true;
        this.detailTab = Tabs.PLANT;
      }
    }
  }

  private onCustomResult(result: IResult) {
    console.warn("Setting custom result!");
    console.debug({ customResult: result });
    this.customResult = result;
  }

  private updateProgress(progress: number) {
    this.$refs.loaderBar.style.width = `${500 * progress}px`;
    if (progress === 1) {
      this.$refs.loaderContainer.remove();
    }
  }

  private highlightAllSelected() {
    (this.$refs.visualization as Visualization).highlightAllSelected();
  }

  private resetCamera() {
    (this.$refs.visualization as Visualization).resetCamera();
  }

  private onReady(instance: IUnityInstance) {
    this.visualizationInstance = instance;
  }

  beforeRouteLeave(to: string, from: string, next: () => void) {
    if (this.visualizationInstance) {
      this.visualizationInstance.Quit().then(next);
      return;
    }

    next();
  }
}
